import { Link } from 'react-router-dom'

import { Icon } from '@weareberyl/design-system'
import { ColumnType } from 'antd/lib/table'
import { VehicleLink } from '~/components/Link'
import { getVehicleTitle } from '~/components/ModuleHeader'
import useSchemes from '~/components/Scheme/useSchemes'
import { hasPermission } from '~/components/Session'
import TableIdCopy from '~/components/shared/TableIdCopy'
import { ICustomColumnProps } from '~/components/Table'
import QueryTable from '~/components/Table/QueryTable'
import { useTableQuery } from '~/components/Table/useTableQuery'
import UserLink, { User } from '~/components/UserLink'
import * as routes from '~/constants/routes'
import {
  FrameType,
  JourneyListQuery_table_JourneyConnection_nodes_Journey as Journey,
  JourneyEvent,
  useJourneyListQuery,
} from '~/gql/generated/graphql'
import { useCurrentUser } from '~/hooks'
import { CURRENT_USER } from '~/types'
import { capitalize, formatDatetime } from '~/utils'
import { isPhone } from '~/utils/mobileDetect'
import { useQueryParams } from '~/utils/useQueryParams'
import { SchemeId } from '../../../constants/domain'
import { EndJourneyButton } from '../actions'

const hasActiveJourney = (msg: JourneyEvent['message']) =>
  ![
    'ended',
    'ended_failed',
    'ended_success',
    'history_ready',
    'charge_ready',
    'docked',
  ].includes(msg)

const getJourneyPath = ({ scheme, id, journey_set }: Journey) => {
  // Prepend the scheme in which this journey took place
  const journeyPath =
    journey_set?.id && (journey_set?.journey_count ?? 1) > 1
      ? `${routes.JOURNEY_SETS}/${journey_set.id}#${id}`
      : `${routes.JOURNEYS}/${id}`
  return scheme ? routes.prependScheme(journeyPath, scheme.id) : journeyPath
}

const getColumns = ({
  module_id: moduleId,
  is_mobile,
  scheme_id,
  omit_user,
}) => {
  const columns: (false | ICustomColumnProps<Journey>)[] = [
    {
      title: 'Type',
      key: 'type',
      dataIndex: ['journey_set', 'journey_count'],
      className: 'sm-hide',
      render: (journey_count: number, journey) => (
        <Link to={getJourneyPath(journey)}>
          <Icon type={journey_count > 1 ? 'groupRide' : 'bikeJourney'} />
        </Link>
      ),
    },
    {
      title: 'ID',
      key: 'id',
      dataIndex: 'id',
      className: 'sm-hide',
      render: (id: string, journey) => (
        <Link to={getJourneyPath(journey)}>
          <TableIdCopy id={id} />
        </Link>
      ),
    },
    {
      title: 'State',
      key: 'state',
      dataIndex: ['state', 'message'],
      render: (msg: string, journey: Journey) => {
        return (
          <Link
            style={{
              color: hasActiveJourney(msg) ? '#ffb600' : undefined,
            }}
            to={getJourneyPath(journey)}
          >
            {msg && capitalize(msg)}
          </Link>
        )
      },
    },
    !omit_user && {
      title: 'User',
      dataIndex: ['user', 'email'],
      render: (email, journey) => (
        <UserLink user={journey?.user as User}>{email}</UserLink>
      ),
    },
    !scheme_id && {
      title: 'Scheme',
      dataIndex: ['scheme', 'name'],
    },
    !moduleId && {
      title: 'Module ID',
      dataIndex: ['vehicle', 'detail', 'module', 'id'],
      render: (module_id: string, { scheme }) => (
        <VehicleLink id={module_id} bikeScheme={scheme ?? { id: scheme_id }} />
      ),
    },
    !moduleId && {
      title: 'Frame',
      render: ({ vehicle }: Journey) =>
        getVehicleTitle(
          vehicle.frame_id,
          vehicle.frame_type as FrameType,
          vehicle.detail.module?.id,
        ),
    },
    !is_mobile && {
      title: 'Last Action',
      dataIndex: ['state', 'time'],
      className: 'xs-hide',
      render: time => formatDatetime(time),
    },
    !is_mobile && {
      title: 'Actions',
      className: 'xs-hide',
      render: ({ id, is_complete, scheme }: Journey) => {
        const canEnd = !is_complete && scheme
        return canEnd ? <EndJourneyButton journeyId={id} /> : null
      },
    },
  ]

  return columns.filter(c => c)
}

export const shouldOmitUser = (
  schemeId: string | null,
  user?: CURRENT_USER,
  is_mobile?: boolean,
) => {
  if (schemeId === null) {
    return !hasPermission(user?.roles ?? [], 'user.all', null)
  }

  return (
    !hasPermission(user?.roles ?? [], 'user.view', parseInt(schemeId)) ||
    is_mobile === true
  )
}

type Props = {
  user_id?: string
  module_id?: string
  all?: boolean
}
export default function JourneyList({ user_id, module_id, all }: Props) {
  const { currentScheme } = useSchemes()
  const [ui_user] = useCurrentUser()

  // Use curretn scheme if there is one.
  // If viewing all then set scheme_id to null
  const scheme_id = currentScheme && !all ? currentScheme.id : null
  const where = scheme_id
    ? []
    : [{ scheme: { id: { ne: SchemeId.tfl_smartbike } } }]

  const id = 'journeys-table'
  const [{ current, pageSize }, setQueryParams] = useQueryParams(id)
  const is_mobile = isPhone()
  const omit_user = shouldOmitUser(scheme_id, ui_user, is_mobile)
  const tableProps = useTableQuery(useJourneyListQuery, {
    variables: {
      user_id,
      module_id,
      scheme_id,
      omit_user,
      where,
      paginate: {
        page: current,
        per_page: pageSize,
      },
    },
  })

  return (
    <QueryTable
      id={id}
      size="small"
      onChange={({ current, pageSize }) =>
        setQueryParams({ current, pageSize })
      }
      columns={
        getColumns({
          module_id,
          is_mobile,
          scheme_id,
          omit_user,
        }) as ColumnType<Journey>[]
      }
      {...tableProps}
    />
  )
}
